import { MutableRefObject } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LngLatBounds } from 'maplibre-gl'
import { Board, Slap, SlapId, PlaceId, ExpandedSlap, SlapLookupTable, DisplayedCenter } from '../utils/Primitives';
import { getPlace, deleteSlapFromPlace, writeSlapToPlaces } from '../places/Places'
import { getStickerDesign, getOrLoadStickerDesign } from '../stickers/StickerDesigns';
import { writeSlapToObjectives } from '../objectives/Objectives';
import { storePlayerEns } from '../player/Players';
export type LookupSlapProps = {
    boardRef: MutableRefObject<Board>,
    slapId: SlapId;
}

export function lookupSlap(props: LookupSlapProps): Slap | null {
    const { boardRef, slapId } = props
    const slapStore = boardRef.current.slapStore as SlapLookupTable
    const slap = slapStore[slapId]
    if (slap) {
        return slap
    }
    return null
}



export function getLatestSlaps(props: {boardRef: MutableRefObject<Board>, count: number}): Slap[] {
    const { boardRef, count } = props
    const slapStore = boardRef.current.slapStore as SlapLookupTable
    const slaps = Object.values(slapStore)
    const latestSlaps = slaps.slice(-count)
    latestSlaps.reverse()
    return latestSlaps as Slap[]
}


export async function writeSlapToBoard(props: {boardRef: MutableRefObject<Board>, slap: Slap}) {
    const { boardRef, slap } = props
    const stickerId = slap.sticker as number
    const sticker = await getOrLoadStickerDesign({boardRef, stickerId})
    if (!sticker) {
        console.log(`error: sticker ${stickerId} not found`)
        return
    }
    const writeSlapId = slap.id as number
    const slapStore = boardRef.current.slapStore as SlapLookupTable
    if (slapStore[writeSlapId]) {
        return
    }
    slapStore[writeSlapId] = slap
    writeSlapToPlaces({boardRef, slap})
    writeSlapToObjectives({boardRef, slap})
    if (slap.playerEns) {
        storePlayerEns({boardRef, address: slap.player, ens: slap.playerEns})
    }
}


export function deleteSlapFromBoard(props: {boardRef: MutableRefObject<Board>, slap: Slap}) {
    const { boardRef, slap } = props
    if (!slap.id || !slap.place) {
        console.log(`error: slap missing info ${slap.id}, ${slap.place}`)
    }
    const slapStore = boardRef.current.slapStore as SlapLookupTable
    delete slapStore[slap.id]
    deleteSlapFromPlace({boardRef, placeId: slap.place, slapId: slap.id})
}

export function deleteSlapFromBoardById(props: {boardRef: MutableRefObject<Board>, slapId: SlapId, placeId: PlaceId}) {
    const { boardRef, slapId, placeId } = props
    if (!slapId || !placeId) {
        console.log(`error: missing slapId or placeId ${slapId}, ${placeId}`)
        return
    }
    const slapStore = boardRef.current.slapStore as SlapLookupTable
    delete slapStore[slapId]
    deleteSlapFromPlace({boardRef, placeId, slapId})
}


export function useFetchSaveSlapsInBounds(props: {boardRef: MutableRefObject<Board>, displayInfo: DisplayedCenter}):
{data: Slap[], isLoading: boolean, isFetched: boolean} {
    const { boardRef, displayInfo } = props

    const { data, isLoading, isFetched } = useQuery({
        queryKey: ['slapsInBounds', displayInfo.bounds],
        queryFn: async () => {
            const StickercloudApiUrl = process.env.NEXT_PUBLIC_STICKERCLOUD_API as string
            if (!StickercloudApiUrl || StickercloudApiUrl.length === 0) {
                console.error(`Stickercloud API URL is not set`)
                return [] as Slap[];
            }
            const slapsInBoundsUrl = `${StickercloudApiUrl}places-in-bounds`
            const b = displayInfo.bounds as LngLatBounds
            const queryString = `?swLat=${b.getSouth()}&swLng=${b.getWest()}&neLat=${b.getNorth()}&neLng=${b.getEast()}`
            const fullUrl = slapsInBoundsUrl + queryString
            const response = await fetch(fullUrl);

            if (!response.ok) {
              throw new Error(`Failed to fetch data: ${response.statusText}`);
            }
            const responseText = await response.text()
            const jsonFull = JSON.parse(responseText);
            if (!jsonFull['data'] || jsonFull['data'].length === 0) {
                console.log(`No slaps in bounds`)
                return [] as Slap[];
            }
            let slaps = [] as Slap[]
            for (const slap of jsonFull['data']) {
                const timeAndDate = new Date(slap.slappedAt);
                const timeAndDateNumber = timeAndDate.getTime()
                const objectiveIds = []
                if (slap.objectiveIds) {
                    for (const id of slap.objectiveIds) {
                        objectiveIds.push(parseInt(id))
                    }
                }
                const convertedSlap = {
                    id: parseInt(slap.slapId),
                    place: parseInt(slap.slapOriginPlaceId),
                    sticker: parseInt(slap.stickerId),
                    time: timeAndDateNumber,
                    size: parseInt(slap.size),
                    player: slap.playerAddress,
                    playerEns: slap.playerEns,
                    isDraft: false,
                    objectiveIds,
                } as Slap
                await writeSlapToBoard({boardRef, slap: convertedSlap})
                slaps.push(convertedSlap)
            }
            return slaps;
          },
    })

    const result = {
        data: data as Slap[],
        isLoading: isLoading,
        isFetched: isFetched
    }
    return result
}

export function setStoredSlapSubmitted(props: { boardRef: MutableRefObject<Board>, slapId: SlapId }) {
    const { boardRef, slapId } = props;
    const slapStore = boardRef.current.slapStore as SlapLookupTable;
    const slap = slapStore[slapId];
    if (slap) {
        slap.isSubmitted = true;
        slapStore[slapId] = slap;
    }
    // Silently return if the slap doesn't exist
}

export function setDraftSlapPlayer(props: { boardRef: MutableRefObject<Board>, slapId: SlapId, player: string }) {
    const { boardRef, slapId, player } = props;
    const slapStore = boardRef.current.slapStore as SlapLookupTable;
    const slap = slapStore[slapId];
    if (slap && slap.isDraft) {
        slap.player = player;
        slapStore[slapId] = slap;
    }
    // Silently return if the slap doesn't exist or is not a draft
}

export async function getOrLoadSlap(props: {
    boardRef: MutableRefObject<Board>,
    slapId: SlapId;
}): Promise<Slap | null> {
    const { boardRef, slapId } = props;

    // First, try to get the slap from local state
    const localSlap = lookupSlap({ boardRef, slapId });
    if (localSlap) {
        return localSlap;
    }

    // If not found locally, fetch from API
    const StickercloudApiUrl = process.env.NEXT_PUBLIC_STICKERCLOUD_API as string;
    if (!StickercloudApiUrl || StickercloudApiUrl.length === 0) {
        console.error(`Stickercloud API URL is not set`);
        return null;
    }

    const slapUrl = `${StickercloudApiUrl}slap?slapId=${slapId}`;

    try {
        const response = await fetch(slapUrl);
        if (!response.ok) {
            console.error(`[DEBUG] getOrLoadSlap: Failed to fetch slap: ${response.statusText}`);
            throw new Error(`Failed to fetch slap: ${response.statusText}`);
        }

        const jsonData = await response.json();

        if (!jsonData['data'] || jsonData['data'].length === 0) {
            return null;
        }

        const slapData = jsonData['data'];
        const loadedSlap: Slap = {
            id: parseInt(slapData['slapId']),
            place: parseInt(slapData['placeId']),
            sticker: parseInt(slapData['stickerDesignId']),
            time: new Date(slapData['slappedAt']).getTime(),
            size: parseInt(slapData['size']),
            player: slapData['playerAddress'],
            playerEns: slapData['playerEns'],
            isDraft: false,
            objectiveIds: slapData['objectiveIds'] ? slapData['objectiveIds'].map((id: string) => parseInt(id)) : [],
        };


        // Store the fetched slap in the Board
        await writeSlapToBoard({ boardRef, slap: loadedSlap });

        return loadedSlap;
    } catch (error) {
        console.error(`Error fetching slap ${slapId}:`, error);
        return null;
    }
}

// Expanded Slaps

export function getLatestExpandedSlaps(props: {boardRef: MutableRefObject<Board>, count: number}): ExpandedSlap[] {
    const { boardRef, count } = props
    const latestSlaps = getLatestSlaps({boardRef, count})
    return latestSlaps.map((slap) => lookupExpandedSlap({boardRef, slapId: slap.id})).
                                            filter((s) => s !== null) as ExpandedSlap[]
}

function expandSlapToExpandedSlap(boardRef: MutableRefObject<Board>, slap: Slap): ExpandedSlap | null {
    const place = getPlace({ boardRef, placeId: slap.place });
    const sticker = getStickerDesign({ boardRef, stickerId: slap.sticker });

    if (!place || !sticker) {
        console.error(`Failed to load place or sticker for slapId ${slap.id}`);
        return null;
    }

    return {
        place,
        sticker,
        id: slap.id,
        time: slap.time,
        size: slap.size,
        player: slap.player,
        playerEns: slap.playerEns,
        objectiveIds: slap.objectiveIds,
    } as ExpandedSlap;
}

export function lookupExpandedSlap(props: LookupSlapProps): ExpandedSlap | null {
    const { boardRef } = props;
    const slap = lookupSlap(props);
    if (!slap) {
        return null;
    }
    return expandSlapToExpandedSlap(boardRef, slap);
}

export async function getOrLoadExpandedSlap(props: {
    boardRef: MutableRefObject<Board>,
    slapId: SlapId;
}): Promise<ExpandedSlap | null> {
    const { boardRef, slapId } = props;
    const slap = await getOrLoadSlap({ boardRef, slapId });
    if (!slap) {
        return null;
    }
    return expandSlapToExpandedSlap(boardRef, slap);
}

